package com.bikompanion.ui.home

import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.LanguagePreference
import com.bikompanion.domain.MapLayerMode
import com.bikompanion.domain.ThemeMode
import com.bikompanion.ui.settings.SettingsPage
import com.bikompanion.ui.watch.StationNotificationTarget

/** Everything the home screen, or a host acting for it, can ask [HomeViewModel] to do. */
sealed interface HomeScreenActions {
    data class StationNotificationSelected(
        val target: StationNotificationTarget,
    ) : HomeScreenActions

    /** A station link was opened: cold start, new Android intent or a browser history step. */
    data class StationLinkOpened(
        val target: StationFocusTarget,
    ) : HomeScreenActions

    data class StationSelected(
        val target: StationFocusTarget,
    ) : HomeScreenActions

    data object StationDismissed : HomeScreenActions

    data class StationFocusHandled(
        val target: StationFocusTarget,
    ) : HomeScreenActions

    /** What the camera is looking at, nearest network first. */
    data class VisibleNetworksChanged(
        val networkIds: List<String>,
        val zoom: Double,
        val visibleBounds: BoundingBox? = null,
    ) : HomeScreenActions

    data class MapLayerModeSelected(
        val mode: MapLayerMode,
    ) : HomeScreenActions

    data object NetworksRetryRequested : HomeScreenActions

    data object ParkingRetryRequested : HomeScreenActions

    data object WatchedStationsRefreshRequested : HomeScreenActions

    data object AutoRefreshStarted : HomeScreenActions

    data object AutoRefreshStopped : HomeScreenActions

    data class StationWatchRequested(
        val station: BikeStation,
        val bikeType: BikeType,
    ) : HomeScreenActions

    data class StationWatchCancelled(
        val station: BikeStation,
        val bikeType: BikeType,
    ) : HomeScreenActions

    data class StationWatchesReloaded(
        val reportExpired: Boolean = false,
    ) : HomeScreenActions

    /** The host has been handed the first [count] pending watch events. */
    data class StationWatchEventsConsumed(
        val count: Int,
    ) : HomeScreenActions

    data object DebugFoundNotificationRequested : HomeScreenActions

    data class ThemeModeSelected(
        val mode: ThemeMode,
    ) : HomeScreenActions

    data class LanguageSelected(
        val language: LanguagePreference,
    ) : HomeScreenActions

    data class SettingsPageOpened(
        val page: SettingsPage,
    ) : HomeScreenActions

    data object SettingsBack : HomeScreenActions

    data object SettingsClosed : HomeScreenActions

    data object InstallPromptDismissed : HomeScreenActions

    data class RatingBikeNumberChanged(
        val bikeNumber: String,
    ) : HomeScreenActions

    data class RatingElectricToggled(
        val isElectric: Boolean,
    ) : HomeScreenActions

    data class VoteSelected(
        val isPositive: Boolean,
    ) : HomeScreenActions

    data class IssueToggled(
        val issue: BikeIssue,
    ) : HomeScreenActions

    data object SubmitClicked : HomeScreenActions

    data class RatingPositionChanged(
        val latitude: Double,
        val longitude: Double,
        val source: BikeScopeSource,
    ) : HomeScreenActions

    data class RatingNetworkSelected(
        val network: BikeNetwork,
    ) : HomeScreenActions

    data object ToastShown : HomeScreenActions

    data object RatingDialogDismissed : HomeScreenActions
}
